import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Layers, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import type { Point2D, GeoCoord, Viewport } from '@/lib/map/mapGeometry';
import {
  projectToLocalMeters,
  localMetersToGeo,
  metricToCanvas,
  canvasToMetric,
  calculateFitViewport,
  clampViewport,
} from '@/lib/map/mapGeometry';
import type { SatelliteSource } from '@/lib/map/tileRenderer';
import { renderMapBackground } from '@/lib/map/tileRenderer';
import type { TrackShape, TrackAnnotation, SplineSample } from '@/lib/spline';
import {
  sampleClosedSpline,
  computeRibbonBoundaries,
  findClosestParametricT,
  renumberCornersByFinishLine,
} from '@/lib/spline';
import { drawTrackRibbon, drawCenterline, drawFinishLine, drawCornerTrackHighlights } from '@/lib/map/trackGeometry';
import type { CornerHighlightItem } from '@/lib/map/trackGeometry';
import { useI18n } from '@/context/I18nContext';

export interface OverlayUtils {
  toCanvas: (pt: Point2D) => Point2D;
  toMetric: (canvasPt: Point2D) => Point2D;
  viewport: Viewport;
  width: number;
  height: number;
  anchor: GeoCoord;
}

export interface InteractiveTrackMapProps {
  anchor?: GeoCoord;
  trackShape?: TrackShape | null;
  trackAnnotations?: TrackAnnotation[];
  points?: Array<{ lat: number; lng: number }>;
  initialFocus?: 'FULL' | Viewport;
  showSatellite?: boolean;
  satelliteSource?: SatelliteSource;
  onSatelliteSourceChange?: (source: SatelliteSource) => void;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  renderOverlay?: (ctx: CanvasRenderingContext2D, utils: OverlayUtils) => void;
  hitTestInteractive?: (canvasPt: Point2D, metricPt: Point2D) => string | null;
  onDragHandle?: (handleId: string, metricPt: Point2D, canvasPt: Point2D) => void;
  onDragHandleEnd?: (handleId: string) => void;
  onClick?: (canvasPt: Point2D, metricPt: Point2D, geoPt: GeoCoord, e: React.MouseEvent) => void;
  onHover?: (canvasPt: Point2D | null, metricPt: Point2D | null, geoPt: GeoCoord | null) => void;
  cursor?: string;
  fitPadding?: number;
  showControls?: boolean;
  showProviderBadge?: boolean;
  drawBaseRibbon?: boolean;
  drawRibbonFill?: boolean;
  showCorners?: boolean;
  disablePan?: boolean;
  onViewportChange?: (viewport: Viewport) => void;
  focusBounds?: Array<{ lat: number; lng: number }> | null;
}

export const InteractiveTrackMap: React.FC<InteractiveTrackMapProps> = ({
  anchor: propAnchor,
  trackShape,
  trackAnnotations,
  points = [],
  initialFocus = 'FULL',
  showSatellite = true,
  satelliteSource: controlledSource,
  onSatelliteSourceChange,
  className = '',
  style,
  children,
  renderOverlay,
  hitTestInteractive,
  onDragHandle,
  onDragHandleEnd,
  onClick,
  onHover,
  cursor: customCursor,
  fitPadding = 35,
  showControls = true,
  showProviderBadge = true,
  drawBaseRibbon = true,
  drawRibbonFill = true,
  showCorners = false,
  disablePan = false,
  onViewportChange,
  focusBounds = null,
}) => {
  const { t } = useI18n();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Satellite provider state
  const [internalSource, setInternalSource] = useState<SatelliteSource>(showSatellite ? 'GOOGLE' : 'VECTOR');
  const activeSource = controlledSource ?? (showSatellite ? internalSource : 'VECTOR');

  const setSource = useCallback(
    (srcOrUpdater: SatelliteSource | ((prev: SatelliteSource) => SatelliteSource)) => {
      const next = typeof srcOrUpdater === 'function' ? srcOrUpdater(activeSource) : srcOrUpdater;
      setInternalSource(next);
      onSatelliteSourceChange?.(next);
    },
    [activeSource, onSatelliteSourceChange]
  );

  // Compute anchor: fallback to centerline first point, or points first point, or (0,0)
  const anchor = useMemo<GeoCoord>(() => {
    if (propAnchor && (propAnchor.lat !== 0 || propAnchor.lng !== 0)) {
      return propAnchor;
    }
    if (trackShape?.centerline && trackShape.centerline.length > 0) {
      return { lat: trackShape.centerline[0].lat, lng: trackShape.centerline[0].lng };
    }
    if (points.length > 0) {
      return { lat: points[0].lat, lng: points[0].lng };
    }
    return { lat: 0, lng: 0 };
  }, [propAnchor, trackShape, points]);

  // Track shape spline & boundaries
  const hasShape = !!(trackShape && trackShape.centerline && trackShape.centerline.length >= 3);
  const trackWidthM = trackShape?.trackWidthM || 8.0;

  const splineSamples = useMemo<SplineSample[]>(() => {
    if (!hasShape) return [];
    return sampleClosedSpline(trackShape!.centerline, trackWidthM, 30);
  }, [hasShape, trackShape, trackWidthM]);

  const boundaries = useMemo(() => {
    if (!hasShape) return { inner: [], outer: [], totalLengthM: 0 };
    return computeRibbonBoundaries(trackShape!.centerline, trackWidthM, 30);
  }, [hasShape, trackShape, trackWidthM]);

  const finishLineAnnot = useMemo(() => {
    if (!hasShape) return null;
    return (trackAnnotations || []).find(a => a.type === 'finish_line') || null;
  }, [hasShape, trackAnnotations]);

  const finishClosest = useMemo(() => {
    if (!finishLineAnnot?.location || splineSamples.length === 0) return null;
    return findClosestParametricT(finishLineAnnot.location, splineSamples);
  }, [finishLineAnnot, splineSamples]);

  const cornerAnnotations = useMemo(() => {
    if (!showCorners) return [];
    return renumberCornersByFinishLine(trackAnnotations || [], splineSamples).filter(
      a => a.type === 'corner'
    );
  }, [showCorners, trackAnnotations, splineSamples]);

  // Metric points for fitting viewport
  const allMetricPoints = useMemo<Point2D[]>(() => {
    if (hasShape && boundaries.inner.length > 0) {
      const pts: Point2D[] = [];
      boundaries.inner.forEach(p => pts.push(projectToLocalMeters(p, anchor)));
      boundaries.outer.forEach(p => pts.push(projectToLocalMeters(p, anchor)));
      return pts;
    }
    if (points.length > 0) {
      return points.map(p => projectToLocalMeters(p, anchor));
    }
    return [{ x: 0, y: 0 }];
  }, [hasShape, boundaries, points, anchor]);

  // Camera viewport state: center in metric coordinates, scale in pixels per meter
  const [viewport, setViewportState] = useState<Viewport>({
    center: { x: 0, y: 0 },
    scale: 0.5,
  });

  const setViewport = useCallback(
    (updater: Viewport | ((prev: Viewport) => Viewport)) => {
      setViewportState(prev => {
        const rawNext = typeof updater === 'function' ? updater(prev) : updater;
        const canvas = canvasRef.current;
        const rect = canvas?.getBoundingClientRect();
        const next =
          rect && rect.width > 0 && rect.height > 0
            ? clampViewport(rawNext, allMetricPoints, rect.width, rect.height, fitPadding)
            : rawNext;
        onViewportChange?.(next);
        return next;
      });
    },
    [allMetricPoints, fitPadding, onViewportChange]
  );

  const hasInitializedRef = useRef(false);
  const hadFocusBoundsRef = useRef(false);
  const tileCacheRef = useRef<Map<string, HTMLImageElement>>(new Map());
  const rafRef = useRef<number | null>(null);
  const [renderVersion, setRenderVersion] = useState(0);

  const requestRedraw = useCallback(() => {
    if (rafRef.current === null) {
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        setRenderVersion(v => v + 1);
      });
    }
  }, []);

  // Viewport fitter
  const initViewport = useCallback(
    (focus: 'FULL' | Viewport, width: number, height: number) => {
      if (allMetricPoints.length === 0 || width <= 0 || height <= 0) return;

      if (typeof focus === 'object') {
        setViewport(focus);
      } else {
        const fit = calculateFitViewport(allMetricPoints, width, height, fitPadding);
        setViewport({
          center: fit.center,
          scale: Math.max(0.01, Math.min(30, fit.scale)),
        });
      }
    },
    [allMetricPoints, fitPadding, setViewport]
  );

  // Reset initialization flag when the underlying track anchor or shape changes
  useEffect(() => {
    hasInitializedRef.current = false;
  }, [anchor.lat, anchor.lng, hasShape]);

  // Initialize camera when canvas dimensions are first ready or track changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0 && !hasInitializedRef.current) {
      initViewport(initialFocus, rect.width, rect.height);
      hasInitializedRef.current = true;
    }
  }, [initViewport, initialFocus, anchor.lat, anchor.lng, hasShape]);

  // Zoom to focusBounds when provided, or reset to FULL when cleared
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;

    if (focusBounds && focusBounds.length > 0) {
      hadFocusBoundsRef.current = true;
      const focusMetric = focusBounds.map(p => projectToLocalMeters(p, anchor));
      const fit = calculateFitViewport(focusMetric, rect.width, rect.height, 75);
      setViewport({
        center: fit.center,
        scale: Math.max(1.2, Math.min(7.5, fit.scale)),
      });
    } else if (hadFocusBoundsRef.current) {
      hadFocusBoundsRef.current = false;
      initViewport('FULL', rect.width, rect.height);
    }
  }, [focusBounds, anchor, setViewport, initViewport]);

  // Handle container resize
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          if (!hasInitializedRef.current) {
            initViewport(initialFocus, width, height);
            hasInitializedRef.current = true;
          } else {
            setViewport(prev => prev);
          }
          requestRedraw();
        }
      }
    });

    observer.observe(canvas);
    return () => observer.disconnect();
  }, [initViewport, initialFocus, requestRedraw, setViewport]);

  // Pointer dragging & interactive handle state
  const isDraggingMapRef = useRef(false);
  const activeHandleRef = useRef<string | null>(null);
  const dragStartRef = useRef<{ clientX: number; clientY: number; center: Point2D; hasDragged: boolean } | null>(null);
  const [cursorStyle, setCursorStyle] = useState<'grab' | 'grabbing' | 'crosshair' | 'pointer' | 'default'>('grab');

  // Touch gesture state (pinch-to-zoom)
  const pinchStartRef = useRef<{
    dist: number;
    scale: number;
    midMetric: Point2D;
  } | null>(null);

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (pinchStartRef.current) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    const clickMetric = canvasToMetric(
      { x: clickX, y: clickY },
      viewport.center,
      viewport.scale,
      rect.width,
      rect.height
    );

    // Check if clicking an interactive handle
    const handleId = hitTestInteractive?.({ x: clickX, y: clickY }, clickMetric);
    if (handleId) {
      activeHandleRef.current = handleId;
      setCursorStyle('pointer');
      e.currentTarget.setPointerCapture(e.pointerId);
      return;
    }

    if (disablePan) return;

    // Track pointer start without immediately engaging pan drag
    isDraggingMapRef.current = false;
    dragStartRef.current = {
      clientX: e.clientX,
      clientY: e.clientY,
      center: { ...viewport.center },
      hasDragged: false,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (pinchStartRef.current) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const cursorX = e.clientX - rect.left;
    const cursorY = e.clientY - rect.top;

    const cursorMetric = canvasToMetric(
      { x: cursorX, y: cursorY },
      viewport.center,
      viewport.scale,
      rect.width,
      rect.height
    );
    const cursorGeo = localMetersToGeo(cursorMetric, anchor);

    // 1. Dragging interactive handle
    if (activeHandleRef.current) {
      onDragHandle?.(activeHandleRef.current, cursorMetric, { x: cursorX, y: cursorY });
      return;
    }

    // 2. Map drag initiation & panning
    if (dragStartRef.current) {
      const dx = e.clientX - dragStartRef.current.clientX;
      const dy = e.clientY - dragStartRef.current.clientY;

      if (!dragStartRef.current.hasDragged) {
        if (Math.hypot(dx, dy) > 5) {
          dragStartRef.current.hasDragged = true;
          isDraggingMapRef.current = true;
          setCursorStyle('grabbing');
        }
      }

      if (isDraggingMapRef.current) {
        const startCenter = dragStartRef.current.center;
        const nextViewport = clampViewport(
          {
            center: {
              x: startCenter.x - dx / viewport.scale,
              y: startCenter.y + dy / viewport.scale,
            },
            scale: viewport.scale,
          },
          allMetricPoints,
          rect.width,
          rect.height,
          fitPadding
        );
        dragStartRef.current = {
          clientX: e.clientX,
          clientY: e.clientY,
          center: nextViewport.center,
          hasDragged: true,
        };
        setViewport(nextViewport);
        return;
      }
    }

    // 3. Hover state & dynamic cursor
    if (hitTestInteractive) {
      const hit = hitTestInteractive({ x: cursorX, y: cursorY }, cursorMetric);
      setCursorStyle(hit ? 'pointer' : 'grab');
    }

    onHover?.({ x: cursorX, y: cursorY }, cursorMetric, cursorGeo);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const wasMapDragged = dragStartRef.current?.hasDragged ?? false;
    const finishedHandle = activeHandleRef.current;

    if (finishedHandle) {
      onDragHandleEnd?.(finishedHandle);
    }

    isDraggingMapRef.current = false;
    activeHandleRef.current = null;
    dragStartRef.current = null;
    setCursorStyle('grab');

    try {
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
    } catch {
      // Ignored
    }

    // If it was a genuine click without dragging a handle or panning the map
    if (!wasMapDragged && !finishedHandle) {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;
      const metricPt = canvasToMetric(
        { x: clickX, y: clickY },
        viewport.center,
        viewport.scale,
        rect.width,
        rect.height
      );
      const geoPt = localMetersToGeo(metricPt, anchor);
      onClick?.({ x: clickX, y: clickY }, metricPt, geoPt, e);
    }
  };

  const handlePointerLeave = () => {
    if (!isDraggingMapRef.current && !activeHandleRef.current) {
      onHover?.(null, null, null);
    }
  };

  // Non-passive wheel event listener for trackpad pinch zoom, trackpad 2-finger pan, and mouse wheel zoom.
  // Must use a native DOM listener with { passive: false } because React attaches onWheel listeners
  // passively, which ignores e.preventDefault() and allows browsers (especially on macOS) to zoom or
  // scroll the entire web page.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let isTrackpadGesture = false;
    let lastWheelTime = 0;

    const onWheel = (e: WheelEvent) => {
      // Completely prevent browser from scrolling or zooming the web page
      e.preventDefault();
      e.stopPropagation();

      const rect = canvas.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;

      const now = performance.now();
      if (now - lastWheelTime > 300) {
        isTrackpadGesture = false;
      }
      lastWheelTime = now;

      // Detect trackpad pinch vs two-finger scroll vs physical mouse wheel
      const isPinch = e.ctrlKey || e.metaKey;

      if (!isPinch && !isTrackpadGesture) {
        // Trackpads emit pixel deltas (deltaMode === 0) with continuous momentum and
        // often non-zero deltaX or fractional/non-notch deltaY.
        // Physical notch mouse wheels typically have deltaMode !== 0 or integer deltaY
        // in multiples of 40/100/120 with deltaX === 0.
        if (e.deltaMode === 0) {
          const absY = Math.abs(e.deltaY);
          const isNotch =
            e.deltaX === 0 &&
            Number.isInteger(e.deltaY) &&
            absY >= 40 &&
            (absY % 40 === 0 || absY % 100 === 0 || absY % 120 === 0);

          if (!isNotch) {
            isTrackpadGesture = true;
          }
        }
      }

      if (isPinch || (!isTrackpadGesture && (e.deltaMode !== 0 || Math.abs(e.deltaY) > 0))) {
        // --- ZOOM ACTION (Trackpad pinch-to-zoom or physical mouse wheel) ---
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        // Exponential zoom for smooth trackpad pinch; power curve for mouse wheel notches
        const zoomFactor = isPinch
          ? Math.exp(-e.deltaY * 0.01)
          : Math.pow(1.002, -e.deltaY);

        setViewport(prev => {
          const fit = calculateFitViewport(allMetricPoints, rect.width, rect.height, fitPadding);
          const minScale = Math.max(0.01, Math.min(30, fit.scale));
          const newScale = Math.max(minScale, Math.min(30, prev.scale * zoomFactor));
          if (newScale === prev.scale) return prev;

          // Invariant: metric point under cursor remains at (mouseX, mouseY)
          const pt = canvasToMetric(
            { x: mouseX, y: mouseY },
            prev.center,
            prev.scale,
            rect.width,
            rect.height
          );
          const newCenterX = pt.x - (mouseX - rect.width / 2) / newScale;
          const newCenterY = pt.y + (mouseY - rect.height / 2) / newScale;

          return {
            center: { x: newCenterX, y: newCenterY },
            scale: newScale,
          };
        });
      } else {
        // --- PAN ACTION (Trackpad two-finger scroll) ---
        if (disablePan) return;

        setViewport(prev => ({
          center: {
            x: prev.center.x + e.deltaX / prev.scale,
            y: prev.center.y - e.deltaY / prev.scale,
          },
          scale: prev.scale,
        }));
      }
    };

    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      canvas.removeEventListener('wheel', onWheel);
    };
  }, [allMetricPoints, disablePan, fitPadding, setViewport]);

  // Multi-touch pinch-to-zoom
  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (e.touches.length >= 2) {
      isDraggingMapRef.current = false;
      activeHandleRef.current = null;
      dragStartRef.current = null;

      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const t0 = e.touches[0];
      const t1 = e.touches[1];

      const dist = Math.hypot(t0.clientX - t1.clientX, t0.clientY - t1.clientY);
      const midCanvas = {
        x: (t0.clientX + t1.clientX) / 2 - rect.left,
        y: (t0.clientY + t1.clientY) / 2 - rect.top,
      };
      const midMetric = canvasToMetric(
        midCanvas,
        viewport.center,
        viewport.scale,
        rect.width,
        rect.height
      );

      pinchStartRef.current = {
        dist,
        scale: viewport.scale,
        midMetric,
      };
    }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    const pinch = pinchStartRef.current;
    if (e.touches.length >= 2 && pinch) {
      e.preventDefault();
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const t0 = e.touches[0];
      const t1 = e.touches[1];

      const newDist = Math.hypot(t0.clientX - t1.clientX, t0.clientY - t1.clientY);
      const currentMid = {
        x: (t0.clientX + t1.clientX) / 2 - rect.left,
        y: (t0.clientY + t1.clientY) / 2 - rect.top,
      };

      const fit = calculateFitViewport(allMetricPoints, rect.width, rect.height, fitPadding);
      const minScale = Math.max(0.01, Math.min(30, fit.scale));
      const scaleRatio = newDist / (pinch.dist || 1);
      const newScale = Math.max(minScale, Math.min(30, pinch.scale * scaleRatio));

      const midM = pinch.midMetric;
      const newCenterX = midM.x - (currentMid.x - rect.width / 2) / newScale;
      const newCenterY = midM.y + (currentMid.y - rect.height / 2) / newScale;

      setViewport({
        center: { x: newCenterX, y: newCenterY },
        scale: newScale,
      });
    }
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (e.touches.length < 2) {
      pinchStartRef.current = null;
    }
    if (e.touches.length === 0) {
      isDraggingMapRef.current = false;
      activeHandleRef.current = null;
      dragStartRef.current = null;
    }
  };

  // Button controls
  const handleZoomIn = () => {
    setViewport(prev => ({
      ...prev,
      scale: Math.min(30, prev.scale * 1.45),
    }));
  };

  const handleZoomOut = () => {
    setViewport(prev => ({
      ...prev,
      scale: Math.max(0.01, prev.scale / 1.45),
    }));
  };

  const handleFitTrack = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    initViewport('FULL', rect.width, rect.height);
  };

  // Canvas render loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    if (width <= 0 || height <= 0) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    // Clear background
    ctx.fillStyle = '#0a0a0f';
    ctx.fillRect(0, 0, width, height);

    const { center, scale } = viewport;
    const toCanvas = (pt: Point2D) => metricToCanvas(pt, center, scale, width, height);
    const toMetric = (canvasPt: Point2D) => canvasToMetric(canvasPt, center, scale, width, height);

    // 1. Draw Satellite Imagery or Offline Vector Grid
    renderMapBackground(
      ctx,
      viewport,
      width,
      height,
      anchor,
      activeSource,
      tileCacheRef.current,
      requestRedraw,
      true
    );

    // 2. Draw Track Ribbon (Asphalt Surface & Borders)
    if (drawBaseRibbon && hasShape) {
      drawTrackRibbon(ctx, boundaries.inner, boundaries.outer, anchor, toCanvas, {
        fillColor: drawRibbonFill ? 'rgba(22, 24, 32, 0.92)' : null,
        borderColor: 'rgba(255, 255, 255, 0.25)',
        borderWidth: 1.2,
      });

      // Start/Finish Line
      if (finishClosest) {
        drawFinishLine(ctx, finishClosest, trackWidthM, anchor, toCanvas, 3);
      }

      // Centerline
      drawCenterline(ctx, splineSamples, anchor, toCanvas, 'rgba(124, 92, 252, 0.4)', 2);
    } else if (drawBaseRibbon && points.length > 1) {
      // Fallback GPS Reference Line
      ctx.beginPath();
      ctx.strokeStyle = 'rgba(124, 92, 252, 0.5)';
      ctx.lineWidth = 2;
      points.forEach((p, i) => {
        const cp = toCanvas(projectToLocalMeters(p, anchor));
        if (i === 0) ctx.moveTo(cp.x, cp.y);
        else ctx.lineTo(cp.x, cp.y);
      });
      ctx.stroke();
    }

    // 2.5 Optional Corner Highlights along Track Shape
    if (showCorners && cornerAnnotations.length > 0) {
      const cornerItems: CornerHighlightItem[] = cornerAnnotations.map((a, i) => ({
        index: i,
        orderIndex: i + 1,
        label: String(i + 1),
        apexPoint: a.apex || a.location,
        entryPoint: a.entry,
        exitPoint: a.exit,
      }));
      drawCornerTrackHighlights(ctx, cornerItems, anchor, toCanvas, {
        showBadges: true,
        splineSamples,
        boundaries,
        gpsPoints: points,
      });
    }

    // 3. Draw Pluggable Overlay (Visual Aids)
    if (renderOverlay) {
      renderOverlay(ctx, {
        toCanvas,
        toMetric,
        viewport,
        width,
        height,
        anchor,
      });
    }
  }, [
    viewport,
    anchor,
    activeSource,
    hasShape,
    boundaries,
    splineSamples,
    finishClosest,
    trackWidthM,
    drawBaseRibbon,
    drawRibbonFill,
    points,
    showCorners,
    cornerAnnotations,
    renderOverlay,
    renderVersion,
    requestRedraw,
  ]);

  return (
    <div
      className={`relative w-full h-full select-none overflow-hidden bg-[#0a0a0f] rounded-2xl ${className}`}
      style={{ touchAction: 'none', ...style }}
    >
      <canvas
        ref={canvasRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onPointerLeave={handlePointerLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        className="w-full h-full block"
        style={{ cursor: customCursor || cursorStyle, touchAction: 'none' }}
      />

      {/* Top-Left Provider Badge & Switcher */}
      {showProviderBadge && (
        <div className="absolute top-3 left-3 flex items-center gap-1.5 z-10">
          <div className="bg-bg-base/80 backdrop-blur-md rounded-full px-2.5 py-1 text-[11px] font-mono text-text-primary border border-white/10 flex items-center gap-1.5 shadow-lg">
            <span
              className={`h-2 w-2 rounded-full shadow-xs ${
                activeSource === 'GOOGLE'
                  ? 'bg-emerald-400'
                  : 'bg-blue-400'
              }`}
            />
            {activeSource === 'GOOGLE'
              ? 'Google Satellite'
              : t('trackMap.vector')}
          </div>

          <button
            type="button"
            onClick={() =>
              setSource(s => (s === 'GOOGLE' ? 'VECTOR' : 'GOOGLE'))
            }
            className="bg-bg-base/80 hover:bg-bg-surface backdrop-blur-md border border-white/10 text-[10px] font-mono px-2.5 py-1 rounded-full text-text-secondary hover:text-text-primary transition-colors shadow-lg cursor-pointer flex items-center gap-1.5"
            title={t('trackMap.switchProvider')}
            aria-label={t('trackMap.switchProvider')}
          >
            <Layers className="h-3 w-3 text-text-secondary" />
            <span className="hidden sm:inline">
              {activeSource === 'GOOGLE' ? t('trackMap.vector') : t('trackMap.satellite')}
            </span>
          </button>
        </div>
      )}

      {/* Bottom-Right Controls: [+] Zoom In, [-] Zoom Out, [Fit Track] */}
      {showControls && (
        <div className="absolute bottom-3 right-3 flex items-center gap-0.5 bg-bg-base/80 backdrop-blur-md border border-white/10 rounded-full p-1 shadow-xl z-10">
          <button
            type="button"
            onClick={handleZoomIn}
            className="p-2 sm:p-1.5 hover:bg-white/10 rounded-full text-text-primary transition-colors cursor-pointer flex items-center justify-center"
            title={t('trackMap.zoomIn')}
            aria-label={t('trackMap.zoomIn')}
          >
            <ZoomIn className="h-4 w-4 text-emerald-400" />
          </button>
          <button
            type="button"
            onClick={handleZoomOut}
            className="p-2 sm:p-1.5 hover:bg-white/10 rounded-full text-text-primary transition-colors cursor-pointer flex items-center justify-center"
            title={t('trackMap.zoomOut')}
            aria-label={t('trackMap.zoomOut')}
          >
            <ZoomOut className="h-4 w-4 text-text-secondary hover:text-text-primary" />
          </button>
          <div className="h-4 w-[1px] bg-white/10 mx-0.5" />
          <button
            type="button"
            onClick={handleFitTrack}
            className="p-2 sm:p-1.5 hover:bg-white/10 rounded-full text-text-primary transition-colors cursor-pointer flex items-center justify-center"
            title={t('trackMap.fitTrack')}
            aria-label={t('trackMap.fitTrack')}
          >
            <Maximize2 className="h-4 w-4 text-blue-400" />
          </button>
        </div>
      )}

      {/* Children (e.g. legends, badges, modals) */}
      {children}
    </div>
  );
};
